import React, { useState, useEffect, useMemo } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { ChartBar, ArrowRight, UsersThree, CurrencyDollar, Gauge, ArrowDown, ArrowUp, FilePdf, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatMoney } from '../utils/formatMoney';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/orbit`;

const OrbitAdminPage = () => {
  const { language } = useLanguage();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [processing, setProcessing] = useState(false);
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);

  const t = (fr, en) => language === 'fr' ? fr : en;

  const PDF_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/orbit`;
  const pdfDocs = [
    { label: t('Rapport du Projet', 'Project Report'), url: `${PDF_BASE}/project-report/pdf?lang=${language}`, testid: 'dl-project-report' },
    { label: t('Systeme de Recompenses', 'Rewards System'), url: `${PDF_BASE}/rewards-concept/pdf?lang=${language}`, testid: 'dl-rewards-pdf' },
    { label: t('Business Plan LocalSite', 'LocalSite Business Plan'), url: `${PDF_BASE}/localsite-concept/pdf?lang=${language}`, testid: 'dl-localsite-pdf' },
  ];

  useEffect(() => { fetchStatement(); fetchPayouts(); }, []);

  const fetchStatement = async () => {
    try {
      const token = localStorage.getItem('orbit-token');
      const res = await axios.get(`${API}/admin/financial-statement`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setStatement(res.data);
    } catch { toast.error(t('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  };

  const fetchPayouts = async () => {
    try {
      const token = localStorage.getItem('orbit-token');
      const res = await axios.get(`${API}/admin/payouts`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setPayouts(res.data.payouts || []);
    } catch (e) { console.error('Admin stats fetch error:', e); }
  };

  const handleProcessCommissions = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('orbit-token');
      toast.info(t('Traitement en cours...', 'Processing...'));
      const res = await axios.post(`${API}/admin/process-commissions?period=${selectedPeriod}`, {}, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      const data = res.data;
      
      if (data.anti_doublon?.matrix_blocked && data.anti_doublon?.matching_blocked) {
        toast.warning(t(
          `Periode ${selectedPeriod} deja traitee ! Anti-doublon active.`,
          `Period ${selectedPeriod} already processed! Anti-duplicate active.`
        ));
      } else {
        const matrixTotal = data.matrix_commissions?.total_commissions || 0;
        const matchTotal = data.matching_bonuses?.total_matching_bonuses || 0;
        toast.success(t(
          `Commissions traitees pour ${selectedPeriod} : ${formatMoney(matrixTotal + matchTotal, language)}`,
          `Commissions processed for ${selectedPeriod}: ${formatMoney(matrixTotal + matchTotal, language)}`
        ));
      }
      fetchStatement();
      fetchPayouts();
    } catch { toast.error(t('Erreur', 'Error')); }
    finally { setProcessing(false); }
  };

  // Generate period options (last 6 months + current)
  const periodOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { year: 'numeric', month: 'long' });
      const isProcessed = payouts.some(p => p.period_id === val && p.status === 'completed');
      options.push({ value: val, label, isProcessed });
    }
    return options;
  }, [language, payouts]);

  if (loading) {
    return (
      <OrbitLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </OrbitLayout>
    );
  }

  const rev = statement?.revenue || {};
  const comm = statement?.commissions || {};

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-admin-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('Administration Orbit', 'Orbit Admin')}</h1>
            <p className="text-gray-300 text-sm mt-1">{t('Etat financier du systeme', 'System financial statement')}</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
              className="bg-white border border-[hsl(40,20%,88%)] rounded-lg text-[var(--ls-text)] text-sm px-3 py-2.5"
              data-testid="orbit-period-select">
              {periodOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} {opt.isProcessed ? (language === 'fr' ? '(traite)' : '(processed)') : ''}
                </option>
              ))}
            </select>
            <button onClick={handleProcessCommissions} disabled={processing}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition-colors text-sm font-medium disabled:opacity-50"
              data-testid="orbit-process-commissions-btn">
              <Gauge className="w-4 h-4" />
              {processing 
                ? t('Traitement...', 'Processing...') 
                : t('Calculer les commissions', 'Process Commissions')}
            </button>
            {/* PDF Download Menu */}
            <div className="relative">
              <button onClick={() => setPdfMenuOpen(!pdfMenuOpen)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--ls-terracotta,#E05D3A)] text-white rounded-xl hover:bg-[var(--ls-terracotta-dark,#C44B2A)] transition-colors text-sm font-medium"
                data-testid="pdf-menu-btn">
                <FilePdf className="w-4 h-4" weight="fill" />
                {t('Documents PDF', 'PDF Documents')}
                <CaretDown className={`w-3 h-3 transition-transform ${pdfMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {pdfMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setPdfMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 z-50 w-64 bg-[#1E293B] border border-indigo-500/20 rounded-xl shadow-2xl overflow-hidden"
                    data-testid="pdf-dropdown">
                    {pdfDocs.map((doc, i) => (
                      <a key={doc.testid} href={doc.url} target="_blank" rel="noopener noreferrer"
                        onClick={() => setPdfMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-indigo-500/10 last:border-0"
                        data-testid={doc.testid}>
                        <FilePdf className="w-5 h-5 text-red-400 flex-shrink-0" weight="fill" />
                        <span className="text-sm text-white">{doc.label}</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('Membres actifs', 'Active Members'), value: statement?.total_members || 0, icon: UsersThree, color: 'text-indigo-400' },
            { label: t('Affilies', 'Affiliates'), value: statement?.total_affiliates || 0, icon: UsersThree, color: 'text-emerald-400' },
            { label: t('Revenus totaux', 'Total Revenue'), value: formatMoney(rev.total_revenue || 0, language), icon: CurrencyDollar, color: 'text-green-400' },
            { label: t('Solde net', 'Net Balance'), value: formatMoney(statement?.net_platform_balance || 0, language), icon: ChartBar, 
              color: (statement?.net_platform_balance || 0) >= 0 ? 'text-emerald-400' : 'text-red-400' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                <span className="text-xs text-gray-500">{kpi.label}</span>
              </div>
              <p className="text-2xl font-bold text-white">{typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Financial Statement Table */}
        <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-indigo-500/10">
            <h2 className="text-lg font-semibold text-white">{t('Etat Financier', 'Financial Statement')}</h2>
            <p className="text-xs text-gray-500 mt-1">{t('Systeme', 'System')}: {statement?.system || 'Orbit Matrix 4x7'}</p>
          </div>
          <div className="p-5 space-y-6">
            {/* Revenue Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ArrowDown className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">{t('Revenus (Entrees)', 'Revenue (Income)')}</h3>
              </div>
              <div className="space-y-2 ml-6">
                <div className="flex justify-between py-2 border-b border-indigo-500/5">
                  <span className="text-gray-400 text-sm">{t('Abonnements mensuels', 'Monthly Memberships')} ({statement?.total_members} x {formatMoney(rev.membership_monthly_rate, language)})</span>
                  <span className="text-white font-medium">{formatMoney(rev.total_membership_revenue || 0, language)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-indigo-500/5">
                  <span className="text-gray-400 text-sm">{t("Frais d'affiliation", 'Affiliate Fees')} ({statement?.total_affiliates} x {formatMoney(rev.affiliate_fee, language)})</span>
                  <span className="text-white font-medium">{formatMoney(rev.total_affiliate_fees || 0, language)}</span>
                </div>
                <div className="flex justify-between py-2 bg-emerald-500/5 rounded-lg px-3">
                  <span className="text-emerald-400 font-semibold text-sm">{t('Total Revenus', 'Total Revenue')}</span>
                  <span className="text-emerald-400 font-bold text-lg">{formatMoney(rev.total_revenue || 0, language)}</span>
                </div>
              </div>
            </div>

            {/* Commissions Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ArrowUp className="w-4 h-4 text-red-400" />
                <h3 className="text-sm font-semibold text-red-400 uppercase tracking-wider">{t('Commissions (Sorties)', 'Commissions (Expenses)')}</h3>
              </div>
              <div className="space-y-2 ml-6">
                <div className="flex justify-between py-2 border-b border-indigo-500/5">
                  <span className="text-gray-400 text-sm">{t('Commissions Matrice', 'Matrix Commissions')}</span>
                  <span className="text-white font-medium">{formatMoney(comm.matrix_commissions_paid || 0, language)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-indigo-500/5">
                  <span className="text-gray-400 text-sm">{t('Commissions Fast Start', 'Fast Start Commissions')}</span>
                  <span className="text-white font-medium">{formatMoney(comm.fast_start_commissions_paid || 0, language)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-indigo-500/5">
                  <span className="text-gray-400 text-sm">{t('Matching Bonus', 'Matching Bonuses')}</span>
                  <span className="text-white font-medium">{formatMoney(comm.matching_commissions_paid || 0, language)}</span>
                </div>
                <div className="flex justify-between py-2 bg-red-500/5 rounded-lg px-3">
                  <span className="text-red-400 font-semibold text-sm">{t('Total Commissions', 'Total Commissions')}</span>
                  <span className="text-red-400 font-bold text-lg">{formatMoney(comm.total_commissions_paid || 0, language)}</span>
                </div>
              </div>
            </div>

            {/* Net Balance */}
            <div className={`p-4 rounded-xl ${(statement?.net_platform_balance || 0) >= 0 ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-400">{t('Entrees - Sorties = Solde', 'Income - Expenses = Balance')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{formatMoney(rev.total_revenue || 0, language)} - {formatMoney(comm.total_commissions_paid || 0, language)}</p>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-bold ${(statement?.net_platform_balance || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                    data-testid="orbit-admin-net-balance">
                    {formatMoney(statement?.net_platform_balance || 0, language)}
                  </p>
                  <p className={`text-xs font-semibold mt-1 ${statement?.status === 'POSITIF' ? 'text-emerald-500' : 'text-red-500'}`}
                    data-testid="orbit-admin-status">
                    {statement?.status}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Config Summary */}
        <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-4">{t('Configuration', 'Configuration')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: t('Largeur matrice', 'Matrix Width'), value: statement?.config?.matrix_width || 2 },
              { label: t('Profondeur matrice', 'Matrix Depth'), value: statement?.config?.matrix_depth || 15 },
              { label: t('Abonnement mensuel', 'Monthly Membership'), value: formatMoney(statement?.config?.membership_monthly || 9.95, language) },
              { label: t('Taux commission', 'Commission Rate'), value: `${((statement?.config?.commission_rate_per_orbit || 0.025) * 100).toFixed(1)}%` },
            ].map((item) => (
              <div key={item.label} className="bg-[#1E293B]/60 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="text-lg font-bold text-white mt-1">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Payout History */}
        <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-indigo-500/10">
            <h2 className="text-lg font-semibold text-white">{t('Historique des payouts', 'Payout History')}</h2>
            <p className="text-xs text-gray-500 mt-1">{t('Anti-doublon actif : chaque periode ne peut etre traitee qu\'une seule fois', 'Anti-duplicate active: each period can only be processed once')}</p>
          </div>
          {payouts.length === 0 ? (
            <p className="text-gray-500 text-center py-6">{t('Aucun payout effectue', 'No payouts processed yet')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-500 text-xs uppercase border-b border-indigo-500/10">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('Periode', 'Period')}</th>
                    <th className="px-4 py-3 text-left">{t('Type', 'Type')}</th>
                    <th className="px-4 py-3 text-center">{t('Statut', 'Status')}</th>
                    <th className="px-4 py-3 text-right">{t('Commissions', 'Commissions')}</th>
                    <th className="px-4 py-3 text-right">{t('Nb transactions', '# Transactions')}</th>
                    <th className="px-4 py-3 text-right">{t('Date traitement', 'Processed at')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-indigo-500/10">
                  {payouts.map((p) => (
                    <tr key={p.period_id || p.processed_at} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-indigo-400 font-medium">{p.period_id}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          p.type === 'matrix' ? 'text-indigo-400 bg-indigo-500/15' : 'text-purple-400 bg-purple-500/15'
                        }`}>
                          {p.type === 'matrix' ? 'Matrix' : 'Matching'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          p.status === 'completed' ? 'text-emerald-400 bg-emerald-500/15' 
                            : p.status === 'failed' ? 'text-red-400 bg-red-500/15' 
                            : 'text-amber-400 bg-amber-500/15'
                        }`}>
                          {p.status === 'completed' ? (language === 'fr' ? 'Termine' : 'Completed') 
                            : p.status === 'failed' ? (language === 'fr' ? 'Echoue' : 'Failed')
                            : (language === 'fr' ? 'En cours' : 'Processing')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium">{formatMoney(p.total_commissions || 0, language)}</td>
                      <td className="px-4 py-3 text-right text-gray-400">{(p.commissions_created || p.members_processed || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{p.completed_at ? new Date(p.completed_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </OrbitLayout>
  );
};

export default OrbitAdminPage;
